import { useEffect, useMemo, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { web, errMsg, toList } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, CaptainBadge } from "../../components/app/kit";
import { StartingFive } from "../../components/app/StartingFive";

const ORDER = ["PG", "SG", "SF", "PF", "C", "Unassigned"];
const NONE = [];
const jerseyNum = (p) => { const n = parseInt(p?.jersey, 10); return Number.isNaN(n) ? 999 : n; };
const byJersey = (a, b) => jerseyNum(a) - jerseyNum(b) || String(a.name ?? "").localeCompare(String(b.name ?? ""));
const posOf = (p) => (Array.isArray(p?.positions) && p.positions.length ? p.positions : p?.position ? [p.position] : []);

/** Default: 1st position by jersey, then players listing it as 2nd position (backups). A saved coach order wins,
 *  and players marked Starter always lead their 1st position. */
const depthFor = (players, pos, saved = []) => {
  const base = pos === "Unassigned"
    ? players.filter((p) => !ORDER.includes(posOf(p)[0])).sort(byJersey).map((p) => ({ p, backup: false }))
    : [...players.filter((p) => posOf(p)[0] === pos).sort(byJersey).map((p) => ({ p, backup: false })),
      ...players.filter((p) => posOf(p)[1] === pos).sort(byJersey).map((p) => ({ p, backup: true }))];
  const rank = (x) => { const i = saved.indexOf(x.p.id); return i === -1 ? saved.length + base.indexOf(x) : i; };
  const lead = (x) => (x.p.starter && !x.backup ? 0 : 1);
  return [...base].sort((a, b) => lead(a) - lead(b) || rank(a) - rank(b));
};

/** Puts the reordered visible players back into the full list, keeping hidden (other level) players in place. */
const mergeOrder = (full, visibleIds) => {
  const seen = new Set(visibleIds);
  const next = [...visibleIds];
  return full.map((x) => (seen.has(x.p.id) ? next.shift() : x.p.id));
};

const Row = ({ x, i, pos, canEdit, onDrop }) => {
  const controls = useDragControls();
  const { p, backup } = x;
  const lead = i === 0 && pos !== "Unassigned";
  return (
    <Reorder.Item value={p.id} dragListener={false} dragControls={controls} onDragEnd={onDrop} data-testid={`depth-${pos.toLowerCase()}-${p.id}`}
      className={`flex select-none items-center gap-3 rounded-xl px-3 py-2 text-sm ${lead ? "bg-[#39FF14]/10 text-white ring-1 ring-[#39FF14]/40" : backup ? "border border-dashed border-[#1E293B] bg-[#0F172A] text-slate-300" : "bg-[#162032] text-slate-200"}`}>
      {canEdit && (
        <button type="button" aria-label={`Drag ${p.name}`} data-testid={`depth-handle-${pos.toLowerCase()}-${p.id}`} onPointerDown={(e) => controls.start(e)}
          className="-ml-1 cursor-grab touch-none p-1 text-slate-400 hover:text-[#39FF14] active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
      )}
      <span className="font-mono-gd text-xs text-slate-400">{i + 1}</span>
      <span className="min-w-0 flex-1 truncate">{p.name}</span>
      {p.captain && <CaptainBadge testid={`depth-captain-${pos.toLowerCase()}-${p.id}`} />}
      {p.starter && !backup && <span data-testid={`depth-starter-${pos.toLowerCase()}-${p.id}`} className="font-mono-gd text-[10px] uppercase tracking-wider text-[#7DF3FF]">Starter</span>}
      {backup && <span data-testid={`depth-backup-${pos.toLowerCase()}-${p.id}`} className="font-mono-gd text-[10px] uppercase tracking-wider text-[#00E5FF]">Backup</span>}
      {p.jersey != null && <span className="font-mono-gd text-xs text-slate-400">#{p.jersey}</span>}
    </Reorder.Item>
  );
};

const Column = ({ pos, all, inLevel, saved, canEdit, tid, onSaved }) => {
  const full = useMemo(() => depthFor(all, pos, saved), [all, pos, saved]);
  const visible = useMemo(() => full.filter((x) => inLevel(x.p)), [full, inLevel]);
  const [ids, setIds] = useState(visible.map((x) => x.p.id));
  useEffect(() => { setIds(visible.map((x) => x.p.id)); }, [visible]);
  const byId = Object.fromEntries(visible.map((x) => [x.p.id, x]));
  const starters = visible.filter((x) => !x.backup).length;
  const save = async () => {
    if (ids.join() === visible.map((x) => x.p.id).join()) return;
    try {
      await web.put(`/teams/${tid}/depth-order/${pos}`, { player_ids: mergeOrder(full, ids) });
      toast.success(`${pos === "Unassigned" ? "Unassigned" : pos} order saved`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); setIds(visible.map((x) => x.p.id)); }
  };
  const reset = async () => {
    try { await web.delete(`/teams/${tid}/depth-order/${pos}`); toast.success("Back to the default order"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`depth-col-${pos.toLowerCase()}`} className="rounded-2xl border border-[#1E293B] bg-[#0F172A] p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <p className="font-display text-4xl font-black text-[#39FF14]">{pos === "Unassigned" ? "—" : pos}</p>
        {canEdit && saved.length > 0 && <button type="button" data-testid={`depth-reset-${pos.toLowerCase()}`} onClick={reset} className="inline-flex items-center gap-1 rounded-full border border-[#1E293B] px-2.5 py-1 font-mono-gd text-[10px] uppercase tracking-wider text-slate-300 hover:border-[#39FF14]/60"><RotateCcw className="h-3 w-3" />Default</button>}
      </div>
      <p className="mb-3 font-mono-gd text-[11px] uppercase tracking-widest text-slate-400">{pos === "Unassigned" ? "No 1st position" : `${starters} deep · ${visible.length - starters} backup${visible.length - starters === 1 ? "" : "s"}`}{saved.length > 0 && <span data-testid={`depth-custom-${pos.toLowerCase()}`} className="text-[#39FF14]"> · Coach order</span>}</p>
      {ids.length ? (
        <Reorder.Group axis="y" values={ids} onReorder={setIds} as="ol" className="space-y-2">
          {ids.map((id, i) => byId[id] && <Row key={id} x={byId[id]} i={i} pos={pos} canEdit={canEdit} onDrop={save} />)}
        </Reorder.Group>
      ) : <p className="text-sm text-slate-400">Empty</p>}
    </div>
  );
};

export default function DepthChart() {
  const { membership, inLevel, canEdit, canRun, level } = useAuth();
  const tid = membership?.team_id;
  const roster = useApi(tid && `/teams/${tid}/players`);
  const orders = useApi(tid && `/teams/${tid}/depth-order`, web);
  const all = useMemo(() => toList(roster.data, ["players"]), [roster.data]);
  const saved = orders.data && typeof orders.data === "object" ? orders.data : {};
  const savedFor = (pos) => (Array.isArray(saved[pos]) ? saved[pos] : NONE);
  const starters = Object.fromEntries(ORDER.slice(0, 5).map((pos) => [pos, depthFor(all, pos, savedFor(pos)).find((x) => inLevel(x.p))?.p ?? null]));
  return (
    <div>
      <PageHeader eyebrow={membership?.team_name} title="Depth chart">
        {canRun && !roster.loading && <StartingFive tid={tid} starters={starters} teamName={membership?.team_name} level={level} />}
      </PageHeader>
      <p className="-mt-4 mb-6 max-w-2xl text-sm text-slate-400">
        Players line up by their 1st position, ordered by jersey number, with 2nd-position backups underneath. Players marked Starter on their profile always lead their 1st position.{canEdit ? " Drag the handle to set your own order in any position." : ""}
      </p>
      {roster.loading ? <Loader /> : roster.error ? <ErrorNote error={roster.error} /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
          {ORDER.map((pos) => <Column key={pos} pos={pos} all={all} inLevel={inLevel} saved={savedFor(pos)} canEdit={canEdit} tid={tid} onSaved={orders.reload} />)}
        </div>
      )}
    </div>
  );
}
